/**
 * Shown by the /claude-agents pages when the signed-in user has no managed
 * agents yet. It used to be a gate: "Request Access", then an API-key form
 * that ran /configure by hand, then a "Request Under Review" screen. Digital
 * Workforce is on for every agency now and setup runs on its own
 * (features/digitalworkforce/dwAutoSetup.ts), so this screen just runs that
 * setup the moment it mounts and hands the parent the first agent when it is
 * done. A failure shows the reason and a Retry; there is no pending state.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { FiGrid, FiZap, FiLock, FiRefreshCw, FiPlus, FiAlertCircle } from 'react-icons/fi';
import platformLogo from '../../assets/images/M_Multicolor.svg';
import { listDwAgents } from './claudeAgentApi';
import { ensureDwConfigured } from '../digitalworkforce/dwAutoSetup';
interface Props {
    onActivated: (agentId: string) => void;
    /** Kept for the callers' sake; setup no longer has a pending/approval state. */
    onPending?: () => void;
}
const NO_AGENT_AFTER_SETUP = 'Setup finished but no agent came back. Please try again, or open Workspaces → Digital Workforce.';
const PlatformMark: React.FC<{
    size?: number;
}> = ({ size = 48 }) => (<div style={{
        width: size, height: size, borderRadius: Math.round(size * 0.29),
        background: 'linear-gradient(135deg, #1e1060 0%, #2d1b69 100%)',
        border: '1px solid rgba(139,92,246,0.3)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0, boxShadow: '0 4px 16px rgba(0,0,0,0.35)',
    }}>
    <img src={platformLogo} alt="Insurance Agency AI" style={{ width: size * 0.72, height: size * 0.72, objectFit: 'contain' }}/>
  </div>);
const ClaudeMark: React.FC<{
    size?: number;
}> = ({ size = 48 }) => (<svg width={size} height={size} viewBox="0 0 48 48" fill="none">
    <defs>
      <linearGradient id="cl-bg-g" x1="0" y1="0" x2="48" y2="48" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stopColor="#fb923c"/>
        <stop offset="100%" stopColor="#c2410c"/>
      </linearGradient>
    </defs>
    <rect width="48" height="48" rx="14" fill="url(#cl-bg-g)"/>
    <rect width="48" height="48" rx="14" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="1"/>
    <g transform="translate(24,24)">
      {([0, 45, 90, 135, 180, 225, 270, 315] as const).map(a => (<rect key={a} x="-2.8" y="-20" width="5.6" height="15" rx="2.8" fill="rgba(255,255,255,0.95)" transform={`rotate(${a})`}/>))}
    </g>
  </svg>);
const FEATURES = [
    { icon: <FiGrid size={13}/>, title: 'AMS Integration', desc: 'Policies, clients & claims' },
    { icon: <FiZap size={13}/>, title: 'Custom Skills', desc: 'Agent-specific capabilities' },
    { icon: <FiPlus size={13}/>, title: 'MCP Servers', desc: 'External APIs & portals' },
    { icon: <FiLock size={13}/>, title: 'Enterprise Ready', desc: 'SOC 2 · Secure by design' },
];
const ClaudeAgentRequestPage: React.FC<Props> = ({ onActivated }) => {
    const [busy, setBusy] = useState(true);
    const [error, setError] = useState<string | null>(null);
    // Read at activation time, so a parent that passes a fresh arrow every
    // render (three of the four callers do) neither restarts setup nor gets a
    // stale callback.
    const onActivatedRef = useRef(onActivated);
    useEffect(() => { onActivatedRef.current = onActivated; }, [onActivated]);
    const mountedRef = useRef(false);
    const activatedRef = useRef(false);
    const run = useCallback(async () => {
        setBusy(true);
        setError(null);
        try {
            await ensureDwConfigured();
            const agents = await listDwAgents(false, true);
            if (!mountedRef.current || activatedRef.current)
                return;
            const first = agents.find((a) => a.is_primary_agent) ?? agents[0];
            if (!first) {
                setError(NO_AGENT_AFTER_SETUP);
                return;
            }
            activatedRef.current = true;
            onActivatedRef.current(first.agent_id);
        }
        catch (e: unknown) {
            if (!mountedRef.current)
                return;
            setError(e instanceof Error ? e.message : 'Setup failed');
        }
        finally {
            if (mountedRef.current)
                setBusy(false);
        }
    }, []);
    useEffect(() => {
        mountedRef.current = true;
        void run();
        return () => { mountedRef.current = false; };
    }, [run]);
    const showRetry = !!error && !busy;
    return (<div className="ca-landing">
      <motion.div className="ca-lcard" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}>
        <div className="ca-lcard__glow"/>
        <div className="ca-lcard__glow ca-lcard__glow--purple"/>


        <div className="ca-partner-row">
          <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.15 }}>
            <PlatformMark size={44}/>
          </motion.div>
          <span className="ca-partner-x">×</span>
          <motion.div initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.15 }}>
            <ClaudeMark size={44}/>
          </motion.div>
        </div>
        <div className="ca-partner-label">Momentum AMP × Anthropic Claude</div>

        <div className="ca-lbadge">
          <span className="ca-lbadge__dot"/>
          DIGITAL WORKFORCE
        </div>

        <h1 className="ca-lhero-title">
          {error ? (<>Setup didn't finish</>) : (<>
              Setting up your<br />
              <span className="ca-lhero-accent">Digital Workforce</span>
            </>)}
        </h1>
        <p className="ca-lhero-desc">
          {error
            ? error
            : 'Your agents are being provisioned for this workspace. This happens once and takes around 10-20 seconds; this page continues on its own when it is done.'}
        </p>


        <div className="ca-lfeature-grid">
          {FEATURES.map((f, i) => (<motion.div key={i} className="ca-lfeature-card" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 + i * 0.06 }}>
              <div className="ca-lfc-icon">{f.icon}</div>
              <div className="ca-lfc-body">
                <div className="ca-lfc-title">{f.title}</div>
                <div className="ca-lfc-desc">{f.desc}</div>
              </div>
            </motion.div>))}
        </div>


        <div className="ca-lactions">
          {showRetry ? (<motion.button className="ca-lbtn" onClick={() => { void run(); }} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}>
              <FiRefreshCw size={14}/> Retry setup
            </motion.button>) : (<button className="ca-lbtn" disabled>
              <FiRefreshCw size={14} className="ca-lspin"/> Preparing workspace…
            </button>)}
        </div>

        {showRetry && (<div className="ca-lerror">
            <FiAlertCircle size={13}/><span>If this keeps happening, sign out and back in, then open Workspaces → Digital Workforce.</span>
          </div>)}

        <p className="ca-lnote">
          <FiLock size={11}/>
          Enterprise-grade security · Powered by Anthropic Claude
        </p>
      </motion.div>
    </div>);
};
export default ClaudeAgentRequestPage;
